<script setup>
import axios from '@axios'
import { computed, onMounted, ref } from 'vue'

/*
 * RM-430 part G — Admin → RM via Text: the new ways to turn on RM via text,
 * one switch each. In production each stays off until Twilio approves the
 * campaign update that describes it; switching one on needs no release.
 * Simplified 10/24 (Kapil): only the switchable ways, each "On" or "Off until
 * Twilio approves". The emailed invitation is always on and the application
 * checkbox has its own switch (Admin → Applications): one line says so.
 */
const emit = defineEmits(['change'])
const ways = ref([])
const loading = ref(false)
const busy = ref({})
const error = ref('')

// The ways an admin switches here (not the registered invitation, not RM-427's checkbox).
const switchable = computed(() => ways.value.filter(w => !w.registered && !w.managedElsewhere))
const onCount = computed(() => switchable.value.filter(w => w.open).length)

async function load() {
  loading.value = true
  try {
    const { data } = await axios.get('/agent/admin/sms-consent-methods')
    ways.value = data.methods
    emit('change', data.methods)
  }
  catch (e) {
    error.value = e?.response?.data?.error || 'Couldn\'t load the ways to turn on RM via text.'
  }
  finally {
    loading.value = false
  }
}

async function setOpen(way, open) {
  busy.value = { ...busy.value, [way.key]: true }
  error.value = ''
  try {
    const { data } = await axios.patch('/agent/admin/sms-consent-methods', { key: way.key, open })
    ways.value = data.methods
    emit('change', data.methods)
  }
  catch (e) {
    error.value = e?.response?.data?.error || 'Couldn\'t save that.'
  }
  finally {
    busy.value = { ...busy.value, [way.key]: false }
  }
}

onMounted(load)
</script>

<template>
  <VExpansionPanels class="ways">
    <VExpansionPanel>
      <VExpansionPanelTitle>
        <div class="ways__heading">
          <span class="ways__title">Ways to turn on RM via text</span>
          <span
            v-if="!loading && switchable.length"
            class="ways__count"
          >{{ onCount }} of {{ switchable.length }} on</span>
        </div>
      </VExpansionPanelTitle>
      <VExpansionPanelText>
        <p class="ways__lede">
          Members who ask RM is on everywhere. Keep the others off in production until Twilio approves them. Switching one needs no release.
        </p>
        <VAlert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ error }}
        </VAlert>
        <VSkeletonLoader
          v-if="loading"
          type="list-item-two-line"
        />
        <ul
          v-else
          class="ways__list"
        >
          <li
            v-for="w in switchable"
            :key="w.key"
            class="ways__row"
          >
            <div class="ways__text">
              <span class="ways__label">{{ w.label }}</span>
              <span
                class="ways__status"
                :class="{ 'ways__status--on': w.open }"
              >{{ w.open ? 'On' : 'Off until Twilio approves' }}</span>
            </div>
            <VSwitch
              :model-value="w.open"
              :disabled="!!busy[w.key]"
              :aria-label="w.label"
              class="ways__switch"
              color="primary"
              inset
              hide-details
              density="compact"
              @update:model-value="v => setOpen(w, !!v)"
            />
          </li>
        </ul>
        <p class="ways__note">
          Always on: the emailed invitation. The application checkbox is switched in Admin → Applications.
        </p>
      </VExpansionPanelText>
    </VExpansionPanel>
  </VExpansionPanels>
</template>

<style scoped>
.ways {
  margin-bottom: 24px;
}

.ways__heading {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  padding-right: 8px;
}

.ways__title {
  font-size: 16px;
  font-weight: 600;
}

.ways__count {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.ways__lede {
  margin: 0 0 8px;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.ways__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ways__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.ways__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.ways__switch {
  flex: 0 0 auto;
}

.ways__label {
  font-size: 14px;
}

.ways__status {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.ways__status--on {
  color: rgb(var(--v-theme-success));
}

.ways__note {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>
